import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  ExternalLink,
  Loader2,
  Minimize2,
  RefreshCcw,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { API, authHeaders } from "@/lib/api";
import {
  useAgentTimeline,
  useBriefProgress,
  useLiveCounters,
  useTypicalBriefLatency,
} from "@/hooks/useBriefProgress";
import { useEffect, useMemo, useRef, useState } from "react";

const AGENT_LABEL = {
  source_universe_scanner: "Discovering sources",
  news_scanner: "Fetching articles",
  entity_extractor: "Extracting entities",
  brief_composer: "Composing brief",
  brief_publisher: "Publishing",
};

function StepIcon({ status }) {
  if (status === "completed")
    return <CheckCircle2 className="h-4 w-4 text-hm-accent" strokeWidth={2} />;
  if (status === "failed")
    return <XCircle className="h-4 w-4 text-hm-danger" strokeWidth={2} />;
  if (status === "running")
    return (
      <Loader2
        className="h-4 w-4 text-hm-accent animate-spin"
        strokeWidth={2}
      />
    );
  return (
    <Circle
      className="h-4 w-4"
      strokeWidth={1.75}
      style={{ color: "var(--hm-icon-pending)" }}
    />
  );
}

function ElapsedBadge({ startedAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const secs = Math.max(0, Math.floor((now - startedAt) / 1000));
  return (
    <span
      className="text-[13px] font-medium text-hm-fg-muted"
      data-testid="brief-progress-elapsed"
    >
      Started {secs}s ago
    </span>
  );
}

function ProgressBar({ timeline }) {
  const completed = timeline.filter((t) => t.status === "completed").length;
  const total = timeline.length || 5;
  const running = timeline.some((t) => t.status === "running");
  const pct = Math.min(
    100,
    Math.round(((completed + (running ? 0.5 : 0)) / total) * 100),
  );
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-hm-bg-2"
      data-testid="brief-progress-bar"
    >
      <motion.div
        className="h-full bg-hm-accent"
        initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.4 }}
      />
    </div>
  );
}

function CounterRow({ counters }) {
  const items = [
    ["Sources scanned", counters.sources_scanned || "—"],
    ["URLs found", counters.urls_found || "—"],
    ["Articles fetched", counters.urls_fetched || "—"],
    ["Kept after filter", counters.urls_kept || "—"],
    ["Composed", counters.articles_composed || "—"],
  ];
  return (
    <div
      className="mt-4 grid grid-cols-5 gap-3 border-t border-hm-border pt-4"
      data-testid="brief-progress-counters"
    >
      {items.map(([label, val]) => (
        <div
          key={label}
          className="min-w-0 rounded-md px-3 py-2.5"
          style={{ background: "var(--hm-bg-elev-3)", border: "1px solid var(--hm-border)" }}
        >
          <div className="text-[11px] uppercase tracking-wider text-hm-fg-muted font-semibold">
            {label}
          </div>
          <div className="text-hm-fg mt-1" style={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1 }}>
            {val}
          </div>
        </div>
      ))}
    </div>
  );
}

function LatencyStrip({ elapsedSec }) {
  const medianMs = useTypicalBriefLatency();
  const medianSec = Math.round((medianMs || 120000) / 1000);
  const ratio = elapsedSec / medianSec;
  const label = `${Math.floor(medianSec / 60)}m ${medianSec % 60}s`;

  let tone = "text-hm-fg-muted";
  let weight = "font-normal";
  let msg = `Typical run ~${label} · you're at ${elapsedSec}s`;
  if (ratio > 3) {
    tone = "text-hm-warn";
    weight = "font-semibold";
    msg = `Taking much longer than typical (~${label}) · ${elapsedSec}s elapsed`;
  } else if (ratio > 1.5) {
    tone = "text-hm-warn";
    weight = "font-medium";
    msg = `Taking longer than typical (~${label}) · ${elapsedSec}s elapsed`;
  } else if (elapsedSec < medianSec / 2) {
    msg = `Typical run ~${label} · you're at ${elapsedSec}s · on track`;
  } else {
    msg = `Typical run ~${label} · you're at ${elapsedSec}s · on track`;
  }
  return (
    <div
      className={`mt-3 text-[12px] ${weight} ${tone}`}
      data-testid="brief-progress-latency-strip"
    >
      {msg}
    </div>
  );
}

function ElapsedSecondsHook(startedAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return Math.max(0, Math.floor((now - startedAt) / 1000));
}

/**
 * Main modal shown mid-generation. Reads active state from context. Handles
 * the terminal transitions (done → nav to detail, failed → retry).
 */
export function BriefProgressModal() {
  const { active, start, minimize, dismiss, acknowledgeStall } =
    useBriefProgress();
  const timeline = useAgentTimeline(active);
  const counters = useLiveCounters(active);
  const navigate = useNavigate();
  const [showDoneFlash, setShowDoneFlash] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const elapsedSec = ElapsedSecondsHook(active?.startedAt || Date.now());

  // Guarded one-shot done-transition. Uses a ref so React re-renders (from
  // late-arriving SSE events, counter mutations, showDoneFlash flipping)
  // cannot cancel the 800ms timer. `active?.status` is the ONLY dependency
  // — everything else read inside the setTimeout is captured via refs.
  const doneHandledRef = useRef(false);
  const activeSnapshotRef = useRef(null);
  const countersRef = useRef(counters);
  countersRef.current = counters;
  activeSnapshotRef.current = active;

  useEffect(() => {
    if (!active) {
      // Modal was dismissed / reset — allow future runs to trigger again.
      doneHandledRef.current = false;
      setShowDoneFlash(false);
      return;
    }
    if (active.status !== "done") return;
    if (doneHandledRef.current) return;
    doneHandledRef.current = true;
    setShowDoneFlash(true);
    // Fire-and-forget: no cleanup returns, so React cannot cancel this timer
    // on subsequent re-renders (which happen every SSE event and every
    // counter memo change during the 800ms window).
    setTimeout(() => {
      const snap = activeSnapshotRef.current;
      if (!snap) return;
      const bId = snap.briefId;
      const count =
        snap.brief?.article_count ?? countersRef.current.articles_composed;
      toast.success(
        `Brief ready — ${count || 0} article${count === 1 ? "" : "s"}`,
      );
      navigate(`/daily-briefs/${bId}`);
      // Dismiss LAST so navigation happens before the modal unmounts.
      dismiss();
      // showDoneFlash resets on the next active===null render above.
    }, 800);
  }, [active?.status, active, dismiss, navigate]);

  const onRetry = async () => {
    /*
     * Turn CC · 13 — legacy v1 retry disabled. The corresponding endpoint
     * (`POST /api/daily-briefs/generate?force=true`) was removed with the
     * v1 pipeline suppression. Daily briefs now regenerate automatically
     * via the pipeline_v2 cron at 05:00 SGT — no manual retry needed.
     * This handler stays only because the modal's UI structure is shared
     * with future retry flows.
     */
    toast.info(
      "Manual retry disabled — daily briefs regenerate automatically via pipeline_v2 (05:00 SGT).",
    );
  };

  const visible = active && !active.minimized;

  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          key="modal-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          data-testid="brief-progress-overlay"
        >
          <motion.div
            className="relative w-[640px] max-w-[92vw] rounded-xl border border-hm-border bg-hm-bg-1 p-6 shadow-2xl"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            data-testid="brief-progress-modal"
          >
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-hm-accent" />
                  <h3 className="text-lg font-semibold text-hm-fg">
                    {active.status === "failed"
                      ? "Generation failed"
                      : active.status === "done"
                        ? "Brief ready"
                        : "Generating your daily brief"}
                  </h3>
                </div>
                <div className="mt-1 flex items-center gap-3">
                  <ElapsedBadge startedAt={active.startedAt} />
                  {active.status === "running" ? (
                    <span className="text-[12px] font-medium text-hm-accent">
                      · Streaming live
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {active.status === "running" ? (
                  <button
                    onClick={minimize}
                    className="rounded-md p-1.5 text-hm-fg-muted hover:bg-hm-bg-2 hover:text-hm-fg"
                    aria-label="Continue in background"
                    data-testid="brief-progress-minimize"
                    title="Continue in background"
                  >
                    <Minimize2 className="h-4 w-4" />
                  </button>
                ) : null}
                <button
                  onClick={dismiss}
                  className="rounded-md p-1.5 text-hm-fg-muted hover:bg-hm-bg-2 hover:text-hm-fg"
                  aria-label="Close"
                  data-testid="brief-progress-close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Progress bar */}
            <div className="mt-5" data-testid="brief-progress-bar-wrap">
              <ProgressBar timeline={timeline} />
            </div>

            {/* Latency estimate strip */}
            {active.status === "running" ? (
              <LatencyStrip elapsedSec={elapsedSec} />
            ) : null}

            {/* Stalled banner */}
            {active.stalled && active.status === "running" ? (
              <div
                className="mt-4 flex items-start gap-3 rounded-lg border border-hm-warn/50 bg-hm-warn/5 p-3"
                data-testid="brief-progress-stalled-banner"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-hm-warn" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-hm-warn">
                    Generation is taking longer than expected
                  </div>
                  <div className="mt-1 text-[12px] text-hm-fg-muted">
                    Your brief is still processing but no update in 3 minutes.
                    You can wait, retry, or check logs.
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={acknowledgeStall}
                      className="mono rounded-md border border-hm-border px-2.5 py-1 text-[10px] uppercase tracking-widest text-hm-fg hover:bg-hm-bg-2"
                      data-testid="brief-progress-stalled-wait"
                    >
                      Keep waiting
                    </button>
                    <button
                      onClick={onRetry}
                      disabled={retrying}
                      className="mono rounded-md border border-hm-accent-dim px-2.5 py-1 text-[10px] uppercase tracking-widest text-hm-accent hover:bg-hm-bg-2 disabled:opacity-40"
                      data-testid="brief-progress-stalled-retry"
                    >
                      {retrying ? "Retrying…" : "Retry"}
                    </button>
                    <a
                      href={`/logs?trace_id=${active.runId || ""}`}
                      className="mono flex items-center gap-1 rounded-md border border-hm-border px-2.5 py-1 text-[10px] uppercase tracking-widest text-hm-fg hover:bg-hm-bg-2"
                      data-testid="brief-progress-stalled-logs"
                    >
                      View logs <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Timeline */}
            <div
              className="mt-5 space-y-3"
              data-testid="brief-progress-timeline"
            >
              {timeline.map((row) => (
                <div
                  key={row.agent}
                  className="flex items-start gap-3"
                  data-testid={`brief-progress-step-${row.agent}`}
                >
                  <div className="mt-0.5">
                    <StepIcon status={row.status} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between">
                      <span
                        className={
                          row.status === "completed"
                            ? "text-sm text-hm-fg"
                            : row.status === "running"
                              ? "text-sm text-hm-fg font-semibold"
                              : "text-sm text-hm-fg-muted"
                        }
                      >
                        {AGENT_LABEL[row.agent] || row.agent}
                      </span>
                      {row.latency_ms ? (
                        <span className="text-xs font-medium text-hm-fg-muted">
                          {(row.latency_ms / 1000).toFixed(1)}s
                        </span>
                      ) : null}
                    </div>
                    {row.detail ? (
                      <div className="mono mt-1 truncate text-[12px] text-hm-fg-muted">
                        {row.detail}
                      </div>
                    ) : null}
                    {row.error ? (
                      <div className="mono mt-1 truncate text-[12px] text-hm-danger font-medium">
                        {row.error}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>

            {/* Counters */}
            <CounterRow counters={counters} />

            {/* Success flash overlay */}
            <AnimatePresence>
              {showDoneFlash ? (
                <motion.div
                  className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-hm-bg-1/95"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  data-testid="brief-progress-done-flash"
                >
                  <div className="flex flex-col items-center gap-2">
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <CheckCircle2
                        className="h-12 w-12 text-hm-accent"
                        strokeWidth={1.5}
                      />
                    </motion.div>
                    <div className="text-sm text-hm-fg">
                      Brief ready —{" "}
                      {active.brief?.article_count ??
                        counters.articles_composed ??
                        0}{" "}
                      articles
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>

            {/* Failure footer */}
            {active.status === "failed" ? (
              <div className="mt-5 border-t border-hm-border pt-4">
                <div className="mono mb-3 text-[11px] text-hm-danger">
                  {active.error || "unknown error"}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={dismiss}
                    data-testid="brief-progress-close-fail"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={onRetry}
                    disabled={retrying}
                    data-testid="brief-progress-retry"
                  >
                    {retrying ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCcw className="mr-2 h-4 w-4" />
                    )}
                    Retry
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-5 flex justify-between border-t border-hm-border pt-4">
                <button
                  className="text-[12px] font-medium text-hm-fg-subtle cursor-not-allowed"
                  disabled
                  title="Cancellation available in Phase 5b"
                  data-testid="brief-progress-cancel-disabled"
                >
                  Cancel
                </button>
                {active.status === "running" ? (
                <button
                  type="button"
                  onClick={minimize}
                  className="text-[12px] font-medium text-hm-accent hover:text-hm-accent-dim bg-transparent border-0 px-2 py-1 cursor-pointer"
                  data-testid="brief-progress-continue-bg"
                >
                  Continue in background →
                </button>
                ) : null}
              </div>
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

// (Removed: previous ref-based retry hack — retry now uses the context directly.)

/**
 * Floating mini-widget shown when the modal is minimized. Keeps subscription
 * alive; clicking it restores the modal.
 */
export function BriefProgressWidget() {
  const { active, restore } = useBriefProgress();
  const timeline = useAgentTimeline(active);
  const [autoHide, setAutoHide] = useState(false);

  const doneCount = useMemo(
    () => timeline.filter((t) => t.status === "completed").length,
    [timeline],
  );

  // After a done event while minimized: flash 5s then auto-dismiss
  useEffect(() => {
    if (active?.minimized && active?.status === "done") {
      setAutoHide(true);
      const t = setTimeout(() => {
        // Auto-dismiss the widget after 5s of glory
        // (parent context clears it)
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [active?.minimized, active?.status]);

  const visible = active && active.minimized;
  if (!visible) return null;

  const isDone = active.status === "done";
  const isFail = active.status === "failed";

  return (
    <AnimatePresence>
      <motion.button
        key="brief-progress-widget"
        onClick={restore}
        className={
          "fixed bottom-6 right-6 z-40 flex items-center gap-3 rounded-full border px-4 py-2.5 shadow-lg backdrop-blur-sm " +
          (isFail
            ? "border-hm-danger/50 bg-hm-bg-1/90 text-hm-danger"
            : isDone
              ? "border-hm-accent-dim bg-hm-bg-1/90 text-hm-accent"
              : "border-hm-border bg-hm-bg-1/90 text-hm-fg")
        }
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.22 }}
        data-testid="brief-progress-widget"
        title="Open brief generation modal"
      >
        {isDone ? (
          <CheckCircle2 className="h-4 w-4 text-hm-accent" />
        ) : isFail ? (
          <XCircle className="h-4 w-4" />
        ) : (
          <Loader2 className="h-4 w-4 animate-spin text-hm-accent" />
        )}
        <span className="mono text-[11px] uppercase tracking-widest">
          {isDone
            ? `Brief ready · ${active.brief?.article_count ?? "—"} articles`
            : isFail
              ? "Generation failed"
              : `Generating · ${doneCount}/${timeline.length} steps`}
        </span>
      </motion.button>
    </AnimatePresence>
  );
}
